import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import Layout from "../components/Layout";
import { Card, CardHeader, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import EditCollaboratorModal from "../components/collaborators/EditCollaboratorModal";
import { fmtDateOnly } from "../utils/datetime";

const CollaboratorDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [collaborator, setCollaborator] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const fetchCollaborator = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/collaborators/${id}`);
      setCollaborator(res.data);
    } catch (err) {
      console.error("Erro ao buscar colaborador:", err);
      alert("Erro ao carregar colaborador.");
      navigate("/collaborators");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollaborator();
  }, [id]);

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const response = await api.get("/collaborators/departments");
        setDepartments(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Erro ao carregar departamentos:", error);
        setDepartments([]);
      }
    };

    loadDepartments();
  }, []);

  useEffect(() => {
    const loadManagers = async () => {
      try {
        const response = await api.get("/collaborators/");
        setManagers(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Erro ao carregar gerentes:", error);
        setManagers([]);
      }
    };

    loadManagers();
  }, []);

  const displayName = useMemo(() => {
    if (!collaborator) return "—";
    const name = `${collaborator.first_name || ""} ${collaborator.last_name || ""}`.trim();
    return name || "—";
  }, [collaborator]);

  const formatDate = (value) => fmtDateOnly(value) || "—";

  const badgeClass = (isActive) =>
    isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800";

  const openEdit = () => setIsEditOpen(true);
  const closeEdit = () => setIsEditOpen(false);

  const handleEditSubmit = (formData) => {
    if (!collaborator?.id) return;

    const payload = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email || null,
      birth_date: formData.birth_date || null,
      hire_date: formData.hire_date || null,
      job_title: formData.job_title || null,
      department_id:
        formData.department_id && /^\d+$/.test(String(formData.department_id))
          ? Number(formData.department_id)
          : null,
      department: formData.department || null,
      cost_center: formData.cost_center || null,
      manager_id: formData.manager_id ? Number(formData.manager_id) : null,
      country: formData.country || null,
      is_active: formData.is_active,
    };

    closeEdit();

    api
      .put(`/collaborators/${collaborator.id}`, payload)
      .then(() => fetchCollaborator())
      .catch((error) => {
        console.error("Erro ao atualizar colaborador:", error);
        alert("Erro ao atualizar colaborador.");
      });
  };

  const handleDelete = () => {
    if (!collaborator?.id) return;
    if (!window.confirm(`Tem certeza que deseja excluir "${displayName}"?`)) return;

    closeEdit();

    api
      .delete(`/collaborators/${collaborator.id}`)
      .then(() => navigate("/collaborators"))
      .catch((error) => {
        console.error("Erro ao deletar colaborador:", error);
        alert("Erro ao deletar colaborador.");
      });
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="collaborator-details-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" aria-label="Voltar" onClick={() => navigate("/collaborators")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{displayName}</h1>
              <p className="text-gray-500">Detalhes do colaborador</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={openEdit}>
              <Pencil className="w-4 h-4 mr-2" />
              Editar
            </Button>
            <Button
              variant="ghost"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={handleDelete}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader />
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Carregando...</div>
            ) : !collaborator ? (
              <div className="text-center py-8 text-gray-500">Não encontrado</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-500">E-mail</p>
                  <p className="text-sm font-medium text-gray-900">{collaborator.email || "—"}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${badgeClass(collaborator.is_active)}`}>
                    {collaborator.is_active ? "Ativo" : "Inativo"}
                  </span>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Departamento</p>
                  <p className="text-sm font-medium text-gray-900">{collaborator.department || "—"}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Cargo</p>
                  <p className="text-sm font-medium text-gray-900">{collaborator.job_title || "—"}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Gerente (responde a)</p>
                  <p className="text-sm font-medium text-gray-900">{collaborator.manager_name || "—"}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Centro de custo</p>
                  <p className="text-sm font-medium text-gray-900">{collaborator.cost_center || "—"}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">País</p>
                  <p className="text-sm font-medium text-gray-900">{collaborator.country || "—"}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Nascimento</p>
                  <p className="text-sm font-medium text-gray-900">{formatDate(collaborator.birth_date)}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Admissão</p>
                  <p className="text-sm font-medium text-gray-900">{formatDate(collaborator.hire_date)}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <EditCollaboratorModal
          isOpen={isEditOpen}
          onClose={closeEdit}
          collaborator={collaborator}
          departments={departments}
          managers={managers}
          onSubmit={handleEditSubmit}
          onDelete={handleDelete}
        />
      </div>
    </Layout>
  );
};

export default CollaboratorDetails;
